<script setup lang="ts">
import Dialog from '@/components/Dialog/Dialog.vue';
import EntityLink from '@/components/EntityLink/EntityLink.vue';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { routenames } from '@/router/routenames';
import { useNotificationStore } from '@/stores/notificationStore';
import { useQaEquipmentStore } from '@/stores/qaEquipmentStore';
import { isIOTDevice } from '@/utils/rtSystem';
import { I18nKey } from '@/utils/typings';
import { reloadEquipmentKey } from '@/views/qaEquipment/types';
import {
  getModelLabel,
  getTypeLabel,
  isRegistrablePtwQaEquipment,
  successfullyRegisteredMsg,
} from '@/views/qaEquipment/utils';
import { computed, inject, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import Button from '@/components/buttons/Button.vue';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import { NotificationMessage } from '@/components/notifications/types';

type QaEquipmentRegistrationDialogEmits = {
  (e: 'click:assignment'): void;
  (e: 'click:support'): void;
};
defineEmits<QaEquipmentRegistrationDialogEmits>();
const modelValue = defineModel<boolean>({ type: Boolean, default: false });

const router = useRouter();
const { currentRoute } = router;
const { t } = useTranslation();
const notificationStore = useNotificationStore();
const qaEquipmentStore = useQaEquipmentStore();
const reloadEquipment =
  currentRoute.value.name === routenames.qaEquipment.add.configuration
    ? undefined
    : inject(reloadEquipmentKey);

qaEquipmentStore.registrationSuccess = () => {
  modelValue.value = false;
  if (qaEquipmentStore.deviceForRegistration) {
    notificationStore.success(successfullyRegisteredMsg(qaEquipmentStore.deviceForRegistration));
    qaEquipmentStore.setDeviceForRegistration(undefined);
  }
  reloadEquipment?.();
};

const { register, resetErrorMessage } = qaEquipmentStore;

const errorNotificationMessage = computed<NotificationMessage>(() => {
  const errorMessage = qaEquipmentStore.detailedError?.message;
  const remedyI18n = qaEquipmentStore.detailedError?.remedyI18n;

  const title =
    typeof errorMessage === 'string'
      ? errorMessage
      : errorMessage && t(errorMessage.key, errorMessage.parameters);

  const message = remedyI18n && t(remedyI18n.key, remedyI18n.parameters);

  return {
    type: 'error',
    title,
    message,
  };
});

watch(modelValue, (modelValueNew) => !modelValueNew && resetErrorMessage());

function handleRegisterLater() {
  if (router.currentRoute.value?.name !== routenames.qaEquipment.details) {
    qaEquipmentStore.setDeviceForRegistration(undefined);
  }

  modelValue.value = false;
}

function handleEntityClick() {
  handleRegisterLater();
}
</script>

<template>
  <Dialog
    v-if="
      qaEquipmentStore.deviceForRegistration &&
      isRegistrablePtwQaEquipment(qaEquipmentStore.deviceForRegistration)
    "
    v-model="modelValue"
    aria-label="RegistrationDialog"
    hideClose
    maxWidth="480"
  >
    <template #title>{{ $t('Register') }}</template>
    <template #text>
      <template v-if="qaEquipmentStore.detailedError">
        <InlineNotification
          v-if="qaEquipmentStore.detailedError"
          class="mb-6"
          :value="errorNotificationMessage"
        />

        <Button
          v-if="
            qaEquipmentStore.detailedError.response?.innerException?.i18n?.key ===
            'Exceptions.CsoPortal.NotFound'
          "
          variant="primary"
          class="mb-8"
          :label="$t('RequestAssignmentToCompanyId')"
          @click="$emit('click:assignment')"
        />
        <Button
          v-if="
            qaEquipmentStore.detailedError.response?.innerException?.i18n?.key ===
            'Exceptions.CsoPortal.InternalError'
          "
          variant="secondary"
          class="mb-8"
          :label="$t('ContactPtwCustomerSupport')"
          @click="$emit('click:support')"
        />
      </template>
      <template v-else-if="qaEquipmentStore.csoPortalFullErrorResponse">
        <InlineNotification
          v-if="qaEquipmentStore.csoPortalFullErrorResponse.message"
          class="mb-6"
          :title="qaEquipmentStore.csoPortalFullErrorResponse.message"
          :value="{
            type: 'error',
            message: $t('ContactPtwCustomerSupport'),
          }"
        />
      </template>
      <p v-else class="text-pre-line mb-4">
        {{
          isIOTDevice(qaEquipmentStore.deviceForRegistration)
            ? $t('RegisterEquipmentWithDpinMsg')
            : $t('RegisterEquipmentMsg')
        }}
      </p>
      <p v-if="isIOTDevice(qaEquipmentStore.deviceForRegistration)" class="text-pre-line mb-4">
        {{ $t('RegisterIotEquipmentMsg') }}
      </p>
      <FormLabel :label="`${$t('Name')} | ${$t('ID')}`">
        <EntityLink
          v-if="qaEquipmentStore.detailedError"
          :id="qaEquipmentStore.deviceForRegistration.id"
          :iuid="qaEquipmentStore.deviceForRegistration.iuid"
          :name="qaEquipmentStore.deviceForRegistration.name"
          type="qaEquipment"
          class="w-100 justify-start text-h5"
          @click="handleEntityClick"
        />
        <IdentifierLabel
          v-else
          :label="qaEquipmentStore.deviceForRegistration.name"
          :identifier="qaEquipmentStore.deviceForRegistration.iuid"
        />
      </FormLabel>
      <v-divider class="my-4" />
      <div class="d-flex flex-wrap justify-start mt-4">
        <TextField
          class="w-50"
          :label="$t('Type_one')"
          :modelValue="$t(getTypeLabel(qaEquipmentStore.deviceForRegistration) as I18nKey)"
          :mode="'details'"
        />
        <TextField
          class="w-50"
          :label="$t('Model')"
          :modelValue="getModelLabel(qaEquipmentStore.deviceForRegistration)"
          :mode="'details'"
        />
        <TextField
          class="w-50 gutterTop"
          :label="$t('ArticleNumber')"
          :modelValue="`${qaEquipmentStore.deviceForRegistration.typeNumber}`"
          :mode="'details'"
        />
        <TextField
          class="w-50 gutterTop"
          :label="$t('SerialNumber')"
          :modelValue="qaEquipmentStore.deviceForRegistration.serialNumber"
          :mode="'details'"
        />
        <TextField
          v-if="'dpin' in qaEquipmentStore.deviceForRegistration"
          class="w-50 gutterTop"
          :label="$t('DPIN')"
          :modelValue="qaEquipmentStore.deviceForRegistration.dpin"
          :mode="'details'"
        />
      </div>
    </template>
    <template #actions>
      <Button
        variant="text"
        :label="$t('RegisterLater')"
        :disabled="qaEquipmentStore.registrationPending"
        @click="handleRegisterLater"
      />

      <Button
        variant="primary"
        :label="$t('RegisterNow')"
        :loading="qaEquipmentStore.registrationPending"
        @click="register(qaEquipmentStore.deviceForRegistration)"
      />
    </template>
  </Dialog>
</template>
